import React, { useState, useEffect, useMemo } from 'react';
import { UserData, FollowerGroup } from '../types';
import { CloseIcon, PlusIcon, EditIcon, CheckmarkIcon, TrashIcon, ChevronLeftIcon } from './Icons';
import { Capacitor } from '@capacitor/core';
import { stickerTextStyle } from './StickerText';

const hexToRgba = (hex: string, opacity: number) => {
    try {
        let cleanHex = hex.replace('#', '');
        if (cleanHex.length === 3) {
            cleanHex = cleanHex[0] + cleanHex[0] + cleanHex[1] + cleanHex[1] + cleanHex[2] + cleanHex[2];
        }
        const r = parseInt(cleanHex.substring(0, 2), 16);
        const g = parseInt(cleanHex.substring(2, 4), 16);
        const b = parseInt(cleanHex.substring(4, 6), 16);
        return isNaN(r) || isNaN(g) || isNaN(b) ? null : `rgba(${r}, ${g}, ${b}, ${opacity})`;
    } catch (e) {
        return null;
    }
};


interface FollowerGroupsModalProps {
    isOpen: boolean;
    onClose: () => void;
    userData: UserData;
    usersData: Record<string, UserData>;
    currentUser: string;
    onUpdateGroups: (groups: FollowerGroup[]) => void;
    initialHighlightUser?: string; // If provided, maybe pre-select a certain group or automatically open 'All' highlighting them? 
    // Actually, if provided, we should probably allow an easy way to just add a specific user to groups.
}

export const FollowerGroupsModal = ({ isOpen, onClose, userData, usersData, currentUser, onUpdateGroups, initialHighlightUser }: FollowerGroupsModalProps) => {
    const [groups, setGroups] = useState<FollowerGroup[]>([]);
    const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
    const [isEditingName, setIsEditingName] = useState(false);
    const [editNameValue, setEditNameValue] = useState('');
    const [showQuickCategorize, setShowQuickCategorize] = useState<boolean>(!!initialHighlightUser);
    
    const allConnections = Array.from(new Set([...(userData.friends || []), ...(userData.followers || [])]));

    useEffect(() => {
        if (!isOpen) return;

        let initialGroups: FollowerGroup[] = (userData.followerGroups || []).map((g: any) => ({
            id: g.id,
            name: g.name,
            followers: g.followers || [],
            isDefault: g.isDefault,
            isReadOnlyName: g.isReadOnlyName
        }));
        const friendNickname = userData.friendNickname || userData.followerNickname || 'Friends';
        const defaultAllName = userData.followerNickname || userData.friendNickname || 'Followers';

        // Ensure default 'all' group exists
        let allGroup = initialGroups.find(g => g.id === 'default_all');
        
        if (!allGroup) {
            allGroup = { id: 'default_all', name: defaultAllName, followers: allConnections, isDefault: true, isReadOnlyName: true };
            initialGroups.unshift(allGroup);
        } else {
            // Force sync friends array and name
            allGroup.name = defaultAllName;
            allGroup.followers = allConnections;
        }

        // Ensure others exist if not customized yet and no other groups exist (first run mapping)
        if (!userData.followerGroups) {
            initialGroups.push({ id: 'default_close_friends', name: 'Close Friends', followers: [], isDefault: true });
            initialGroups.push({ id: 'default_acquaintances', name: 'Acquaintances', followers: [], isDefault: true });
            initialGroups.push({ id: 'default_family', name: 'Family', followers: [], isDefault: true });
        }

        setGroups(initialGroups);
        setShowQuickCategorize(!!initialHighlightUser);
    }, [isOpen, userData.followerGroups, userData.friendNickname, userData.followerNickname, userData.friends, userData.followers, initialHighlightUser]);

    const handleSaveGroups = (newGroups: FollowerGroup[]) => {
        setGroups(newGroups);
        onUpdateGroups(newGroups);
    };

    const handleCreateGroup = () => {
        const id = 'custom_' + Date.now().toString();
        const newGroups = [...groups, { id, name: 'New Group', followers: [] }];
        handleSaveGroups(newGroups);
        setActiveGroupId(id);
        setEditNameValue('New Group');
        setIsEditingName(true);
    };

    const handleDeleteGroup = (id: string) => {
        if (window.confirm("Are you sure you want to delete this group? The friends won't be removed from your profile.")) {
            const newGroups = groups.filter(g => g.id !== id);
            handleSaveGroups(newGroups);
            setActiveGroupId(null);
        }
    };

    const handleToggleFollower = (groupId: string, follower: string) => {
        const newGroups = groups.map(g => {
            if (g.id === groupId) {
                const has = g.followers.includes(follower);
                return {
                    ...g,
                    followers: has ? g.followers.filter(f => f !== follower) : [...g.followers, follower]
                };
            }
            return g;
        });
        handleSaveGroups(newGroups);
    };

    const handleToggleGroupForUser = (groupId: string, targetUser: string) => {
        const newGroups = groups.map(g => {
            if (g.id === groupId) {
                // "All" group bypasses standard toggle, as they are implicitly there.
                if (g.id === 'default_all') return g;
                const has = g.followers.includes(targetUser);
                return {
                    ...g,
                    followers: has ? g.followers.filter(f => f !== targetUser) : [...g.followers, targetUser]
                };
            }
            return g;
        });
        handleSaveGroups(newGroups);
    };

    const handleNameSave = (groupId: string) => {
        if (!editNameValue.trim()) return;
        const newGroups = groups.map(g => g.id === groupId ? { ...g, name: editNameValue.trim() } : g);
        handleSaveGroups(newGroups);
        setIsEditingName(false);
    };

    if (!isOpen) return null;

    const activeGroup = groups.find(g => g.id === activeGroupId);
    const themeColor = userData.appThemeColor || '#161b22';
    const textColor = userData.appTextColor || '#ffffff';

    // Quick Categorize View Layer
    if (showQuickCategorize && initialHighlightUser) {
        return (
            <div className="fixed inset-0 z-[200] flex items-center justify-center animate-fade-in p-4 md:p-8 messages-page-no-swipe">
                <div className="absolute inset-0 backdrop-blur-md bg-black/60" onClick={onClose} />
                <div className="relative w-full max-w-md max-h-full flex flex-col rounded-3xl overflow-hidden shadow-2xl border border-white/10" style={{ backgroundColor: themeColor, color: textColor }}>
                    <div className="p-6 border-b border-white/10 flex justify-between items-center bg-black/20" style={{ paddingTop: Capacitor.isNativePlatform() ? 'calc(1.5rem + env(safe-area-inset-top, 0px))' : '1.5rem' }}>
                        <div>
                            <h2 className="text-xl font-black uppercase italic tracking-tighter" style={stickerTextStyle(0.35)}>Categorize Friend</h2>
                            <p className="text-xs opacity-70 mt-1" style={stickerTextStyle(0.18)}>Assign @{initialHighlightUser} to groups</p>
                        </div>
                        <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                            <CloseIcon className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 flex flex-wrap gap-2 no-scrollbar allow-scroll">
                        {groups.map(g => {
                            if (g.id === 'default_all') return null; // Cannot be manually unchecked
                            const isMember = g.followers.includes(initialHighlightUser);
                            return (
                                <button
                                    key={g.id}
                                    onClick={() => handleToggleGroupForUser(g.id, initialHighlightUser)}
                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs transition-colors border ${isMember ? 'bg-blue-500/30 border-blue-400' : 'bg-black/30 border-white/10 hover:bg-white/10'}`}
                                >
                                    <span className="font-bold" style={stickerTextStyle(0.18)}>{g.name}</span>
                                </button>
                            )
                        })}
                        <button
                            onClick={() => {
                                handleCreateGroup();
                                setShowQuickCategorize(false);
                            }}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs transition-colors border border-dashed border-white/20 text-white/50 hover:bg-white/5 hover:text-white"
                            style={stickerTextStyle(0.18)}
                        >
                            <PlusIcon className="w-3 h-3" />
                            Create New Group
                        </button>
                    </div>

                    <div className="p-4 border-t border-white/10 bg-black/20">
                        <button onClick={onClose} className="w-full py-3 bg-white text-black rounded-xl font-bold hover:bg-gray-200 transition-colors" style={stickerTextStyle(0.18)}>
                            Done
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center animate-fade-in p-0 md:p-8 messages-page-no-swipe">
            {/* Backdrop */}
            <div className="absolute inset-0 backdrop-blur-md bg-black/60" onClick={onClose} />

            <div className="relative w-full h-full md:w-[600px] md:h-[800px] max-h-full flex flex-col md:rounded-[2rem] overflow-hidden shadow-2xl border border-white/10" style={{
                backgroundColor: (() => {
                    const user = usersData[currentUser];
                    const baseColor = user?.appThemeColor || '#111';
                    const opacity = (userData.postBlurOpacity ?? 80) / 100;
                    return hexToRgba(baseColor, opacity) || `rgba(20, 20, 30, ${opacity})`;
                })()
            }}>

                {/* Header */}
                <div className="flex-shrink-0 p-4 border-b border-white/10 bg-black/20 flex items-center justify-between" style={{ paddingTop: Capacitor.isNativePlatform() ? 'calc(1rem + env(safe-area-inset-top, 0px))' : '1rem' }}>
                    <div className="flex items-center gap-3">
                        {activeGroupId && (
                            <button onClick={() => setActiveGroupId(null)} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                                <ChevronLeftIcon className="w-5 h-5" />
                            </button>
                        )}
                        <div>
                            <h2 className="text-xl font-black uppercase italic tracking-tighter" style={stickerTextStyle(0.35)}>
                                {activeGroupId ? 'Manage Group' : 'Follower Groups'}
                            </h2>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                        <CloseIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 min-h-0 flex flex-col">
                    {!activeGroup ? (
                        <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar allow-scroll">
                            {groups.map(group => (
                                <div key={group.id} className="group bg-black/20 border border-white/10 rounded-2xl p-4 flex items-center justify-between hover:bg-white/5 transition-colors cursor-pointer" onClick={() => setActiveGroupId(group.id)}>
                                    <div>
                                        <h3 className="font-bold text-lg" style={stickerTextStyle(0.18)}>{group.name}</h3>
                                        <p className="text-xs opacity-60 mt-1" style={stickerTextStyle(0.18)}>{group.followers.length} members</p>
                                    </div>
                                    <ChevronLeftIcon className="w-5 h-5 opacity-40 rotate-180" />
                                </div>
                            ))}

                            <button
                                onClick={handleCreateGroup}
                                className="w-full p-4 rounded-2xl border-2 border-dashed border-white/20 text-white/50 hover:bg-white/5 hover:text-white transition-all flex justify-center items-center gap-2 font-bold mt-4"
                                style={stickerTextStyle(0.18)}
                            >
                                <PlusIcon className="w-5 h-5" />
                                Create Custom Group
                            </button>
                        </div>
                    ) : (
                        <div className="flex-1 flex flex-col min-h-0">
                            <div className="flex-shrink-0 p-6 border-b border-white/5 bg-black/10">
                                <div className="flex items-center justify-between mb-2">
                                    {isEditingName ? (
                                        <div className="flex-1 flex gap-2">
                                            <input
                                                autoFocus
                                                value={editNameValue}
                                                onChange={e => setEditNameValue(e.target.value)}
                                                onKeyDown={e => e.key === 'Enter' && handleNameSave(activeGroup.id)}
                                                className="flex-1 bg-black/40 border border-white/20 rounded-xl px-3 py-2 outline-none focus:border-blue-500 font-bold"
                                                style={stickerTextStyle(0.18)}
                                            />
                                            <button onClick={() => handleNameSave(activeGroup.id)} className="px-4 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700" style={stickerTextStyle(0.18)}>Save</button>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-3">
                                            <h3 className="text-2xl font-black italic uppercase tracking-tighter" style={stickerTextStyle(0.35)}>{activeGroup.name}</h3>
                                            {!activeGroup.isReadOnlyName && (
                                                <button onClick={() => { setEditNameValue(activeGroup.name); setIsEditingName(true); }} className="p-2 hover:bg-white/10 rounded-full text-white/50 hover:text-white">
                                                    <EditIcon className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    {!activeGroup.isDefault && (
                                        <button onClick={() => handleDeleteGroup(activeGroup.id)} className="p-2 hover:bg-red-500/20 text-red-400 rounded-full transition-colors ml-4">
                                            <TrashIcon className="w-5 h-5" />
                                        </button>
                                    )}
                                </div>
                                <p className="text-sm opacity-60" style={stickerTextStyle(0.18)}>
                                    {activeGroup.id === 'default_all' ? "Everyone who follows you is automatically here." : "Select followers to include in this group."}
                                </p>
                            </div>

                            <div className="flex-1 overflow-y-auto p-4 custom-scrollbar allow-scroll">
                                {allConnections.length === 0 ? (
                                    <div className="text-center py-20 opacity-40 italic" style={stickerTextStyle(0.18)}>You don't have any followers yet.</div>
                                ) : (
                                    <div className="flex flex-wrap gap-2">
                                        {allConnections.map(follower => {
                                            const isSelected = activeGroup.followers.includes(follower) || activeGroup.id === 'default_all';
                                            const uData = usersData[follower];

                                            return (
                                                <button
                                                    key={follower}
                                                    onClick={() => {
                                                        if (activeGroup.id !== 'default_all') {
                                                            handleToggleFollower(activeGroup.id, follower);
                                                        }
                                                    }}
                                                    disabled={activeGroup.id === 'default_all'}
                                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs transition-colors border ${isSelected ? 'bg-blue-500/30 border-blue-400' : 'bg-black/30 border-white/10 hover:bg-white/10'}`}
                                                >
                                                    {uData?.pfp ? (
                                                        <img src={uData.pfp} alt={follower} className="w-5 h-5 rounded-full object-cover flex-shrink-0" />
                                                    ) : (
                                                        <div className="w-5 h-5 rounded-full bg-gray-500 flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0">
                                                            {(uData?.displayName || follower)[0].toUpperCase()}
                                                        </div>
                                                    )}
                                                    <span className="font-bold" style={stickerTextStyle(0.18)}>{uData?.displayName || follower}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
